// The office renewal price override (Session AKO, Director decision).
//
// A manual annual figure that replaces the CPI + escalator calculation for THIS contract's next
// renewal cycle: "hold it flat", "a smaller rise than CPI", or any other figure agreed directly with
// the customer. RULE 6: the CPI-calculated figure this would replace is always shown alongside it, so
// setting one is never blind, and the copy below deliberately does not teach it as a routine step;
// most renewals should be left on the automatic calculation, and this panel says so.
//
// UNLIKE RenewalFrequencyPanel, THIS ALWAYS RENDERS for an active contract, override or not: it is a
// discoverable office action, not a review queue that only appears once something is outstanding.
import { useCallback, useEffect, useState } from "react";
import { api, ApiError, type RenewalPriceOverrideView } from "../../lib/api";
import { buttonClasses } from "../../components/Button";
import { ConfirmButton } from "../../components/ConfirmButton";
import { formatDate } from "../../lib/format";

function fmtMoney(n: number): string {
  return `£${Math.round(n).toLocaleString("en-GB")}`;
}

function cpiWouldText(cpiWould: RenewalPriceOverrideView["cpiWould"]): string {
  if (cpiWould.available) return `CPI would currently price this renewal at ${fmtMoney(cpiWould.annualValue)} a year.`;
  const reason =
    cpiWould.reason === "cpi_unavailable"
      ? "no published CPI figure is available"
      : cpiWould.reason === "rate_not_recorded"
        ? "this contract has no recorded annual uplift rate"
        : "the platform's escalator floor and cap are not configured";
  return `The usual CPI calculation cannot price this renewal right now, because ${reason}.`;
}

export function RenewalPriceOverridePanel({ contractId }: { contractId: string }) {
  const [view, setView] = useState<RenewalPriceOverrideView | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [annual, setAnnual] = useState("");
  const [reason, setReason] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setView(await api.contracts.renewalPriceOverride(contractId));
    } catch {
      // A read failure must not break the contract page.
      setView(null);
    } finally {
      setLoading(false);
    }
  }, [contractId]);

  useEffect(() => {
    void load();
  }, [load]);

  const set = useCallback(async () => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const parsed = Number(annual.replace(/[^0-9.]/g, ""));
      if (!Number.isFinite(parsed) || parsed <= 0) {
        setError("Enter a positive annual figure.");
        setBusy(false);
        return;
      }
      const res = await api.contracts.setRenewalPriceOverride(contractId, {
        annualValue: parsed,
        ...(reason.trim() ? { reason: reason.trim() } : {}),
      });
      setView(res);
      setEditing(false);
      setAnnual("");
      setReason("");
      setNotice(`Override set. This renewal will show ${fmtMoney(parsed)} a year until it is cleared or the renewal is signed.`);
    } catch (err) {
      setError(
        err instanceof ApiError && typeof err.code === "string" && err.code.includes(" ")
          ? err.code
          : "Could not set the override. Please try again.",
      );
    } finally {
      setBusy(false);
    }
  }, [annual, reason, contractId]);

  const clear = useCallback(async () => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const res = await api.contracts.clearRenewalPriceOverride(contractId);
      setView(res);
      setNotice("Override cleared. This renewal is back on the automatic CPI calculation.");
    } catch {
      setError("Could not clear the override. Please try again.");
    } finally {
      setBusy(false);
    }
  }, [contractId]);

  if (loading || !view) return null;

  const overrideBy = view.override
    ? [formatDate(view.override.setAt), view.override.setByName ? `by ${view.override.setByName}` : null].filter(Boolean).join(" ")
    : "";

  return (
    <section className="ele-card mt-4 p-4">
      <h3 className="font-heading text-sm uppercase tracking-wide text-ele-strong">Renewal price override</h3>
      <p className="mt-2 text-sm text-ele-body">
        Set a specific price for this contract's next renewal, replacing the automatic CPI calculation for this one cycle.
        Most renewals should be left as they are. Only use this once a figure has actually been agreed with the customer.
      </p>
      {/* Session AQW (M-1): the one added sentence -- this override only ever feeds mechanism A (the
          customer's own interactive renewal link). It never reaches the CPI-only proposal ledger on
          the Renewals page or a manually drafted renewal letter, which is a real, existing limitation
          of the code today, not a new restriction. */}
      <p className="mt-1 text-sm text-ele-body">
        This is the price offered through the renewal link. It does not change a rise proposed on the Renewals page, or the
        figure in a drafted renewal letter.
      </p>

      {!view.eligible ? (
        <p className="mt-2 text-sm text-ele-body">
          Only an active contract can have an override set. This one is {view.status.replace("_", " ")}.
        </p>
      ) : (
        <>
          <p className="mt-2 text-xs text-ele-body">{cpiWouldText(view.cpiWould)}</p>

          {view.override ? (
            <div className="mt-3 rounded-ele border border-ele-hairline bg-ele-surface p-3">
              <p className="text-sm text-ele-strong">
                Override active: <b>{fmtMoney(view.override.annualValue)}</b> a year.
              </p>
              <p className="mt-1 text-xs text-ele-body">
                Set {overrideBy}.{view.override.reason ? ` Note: ${view.override.reason}` : ""}
              </p>
              <ConfirmButton
                onConfirm={() => void clear()}
                busy={busy}
                busyLabel="Clearing..."
                label="Clear override"
                confirmLabel="Press again to clear"
                className="mt-2 rounded-ele border border-ele-hairline px-3 py-1.5 font-menu text-xs tracking-wide text-ele-strong transition hover:opacity-90 disabled:opacity-50"
                armedClassName="font-semibold ring-2 ring-ele-blue"
              />
            </div>
          ) : null}

          {notice ? (
            <p role="status" className="mt-2 text-sm text-ele-green">
              {notice}
            </p>
          ) : null}
          {error ? (
            <p role="alert" className="mt-2 text-sm text-ele-danger">
              {error}
            </p>
          ) : null}

          {editing ? (
            <div className="mt-3">
              <label className="block text-xs uppercase tracking-wide text-ele-body" htmlFor="renewal-price-override-annual">
                Agreed annual charge, ex VAT
              </label>
              <input
                id="renewal-price-override-annual"
                inputMode="decimal"
                value={annual}
                onChange={(e) => setAnnual(e.target.value)}
                placeholder={String(
                  Math.round(view.override ? view.override.annualValue : view.cpiWould.available ? view.cpiWould.annualValue : view.currentAnnual),
                )}
                className="mt-1 w-40 rounded-ele border border-ele-hairline bg-ele-surface px-2 py-1.5 text-sm text-ele-strong"
              />
              <label className="mt-2 block text-xs uppercase tracking-wide text-ele-body" htmlFor="renewal-price-override-reason">
                Note for the office record (optional, never shown to the customer)
              </label>
              <input
                id="renewal-price-override-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g. agreed on the phone, holding flat for a year"
                className="mt-1 w-full max-w-sm rounded-ele border border-ele-hairline bg-ele-surface px-2 py-1.5 text-sm text-ele-strong"
              />
              <div className="mt-3 flex flex-wrap gap-2">
                <ConfirmButton
                  onConfirm={() => void set()}
                  busy={busy}
                  busyLabel="Saving..."
                  label="Set override"
                  confirmLabel="Press again to set"
                  className={buttonClasses("primary", "md")}
                  armedClassName="font-semibold ring-2 ring-ele-blue"
                />
                <button
                  type="button"
                  onClick={() => {
                    setEditing(false);
                    setError(null);
                  }}
                  className="max-sm:min-h-11 rounded-ele border border-ele-hairline px-4 py-2 font-menu text-xs tracking-wide text-ele-strong transition hover:opacity-90"
                >
                  Cancel
                </button>
              </div>
              <p className="mt-2 text-xs text-ele-body">
                Nothing is sent to the customer. An already-issued renewal link picks this up automatically.
              </p>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setEditing(true)}
              className="mt-3 rounded-ele border border-ele-hairline px-3 py-1.5 font-menu text-xs tracking-wide text-ele-strong transition hover:opacity-90"
            >
              {view.override ? "Change override" : "Set an override"}
            </button>
          )}
        </>
      )}
    </section>
  );
}
